import type { Cache } from '@urql/exchange-graphcache';
import { DateTime } from 'luxon';
import { ChatMessageItemFragment } from '@/components/Chat/ChatMessageItem.utils';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import chatHomeListCacheConfig from './chatHomeListCacheConfig';
import { QUERY_ROOT_NAME } from './constants';
import insertIntoCachedListBaseQueries from './insertIntoCachedListBaseQueries';

const ChatLatestMessageFragment = graphql(/* GraphQL */ `
  fragment ChatLatestMessage on chat {
    __typename
    id
    messages(
      limit: 1
      order_by: { created_at: desc }
      where: { deleted_at: { _is_null: true } }
    ) {
      ...ChatMessageItem
    }
  }
`, [ChatMessageItemFragment]);

export default function linkMessages(
  cache: Cache,
  links: FragmentOf<typeof ChatMessageItemFragment>[],
) {
  const chatIds = new Set<string>();

  links.forEach((item) => {
    const message = readFragment(ChatMessageItemFragment, item);
    chatIds.add(message.chat_id);

    const chatFragment = cache.readFragment(ChatLatestMessageFragment, {
      __typename: 'chat',
      id: message.chat_id,
    });

    const firstChatMessageItem = readFragment(
      ChatMessageItemFragment,
      chatFragment?.messages[0],
    );

    if (
      !firstChatMessageItem?.created_at ||
      DateTime.fromISO(firstChatMessageItem.created_at).toMillis() <
        DateTime.fromISO(message.created_at).toMillis()
    ) {
      cache.writeFragment(ChatLatestMessageFragment, {
        __typename: 'chat',
        id: message.chat_id,
        messages: [item],
      });
    }
  });

  insertIntoCachedListBaseQueries(cache, chatIds, chatHomeListCacheConfig);

  const headPages = new Map<string, { fieldKey: string; cursor: number }>();
  for (const field of cache.inspectFields(QUERY_ROOT_NAME)) {
    if (field.fieldName !== 'message' || !field.arguments) continue;
    const where = field.arguments.where;
    if (!isRecord(where) || Object.keys(where).length !== 2) continue;
    const chatPredicate = where.chat_id;
    const cursorPredicate = where.created_at;
    if (
      !isRecord(chatPredicate) ||
      !isRecord(cursorPredicate) ||
      Object.keys(chatPredicate).length !== 1 ||
      Object.keys(cursorPredicate).length !== 1
    )
      continue;
    const chatId = chatPredicate._eq;
    const cursorValue = cursorPredicate._lt;
    if (typeof chatId !== 'string' || typeof cursorValue !== 'string') continue;
    if (!chatIds.has(chatId)) continue;
    const cursor = Date.parse(cursorValue);
    if (!Number.isFinite(cursor)) continue;
    const previousHead = headPages.get(chatId);
    if (!previousHead || cursor > previousHead.cursor) {
      headPages.set(chatId, { fieldKey: field.fieldKey, cursor });
    }
  }

  for (const [chatId, head] of headPages) {
    const existing = cache.resolve(QUERY_ROOT_NAME, head.fieldKey);
    if (!Array.isArray(existing)) continue;
    const existingLinks = existing.filter(
      (link): link is string => typeof link === 'string',
    );
    if (existingLinks.length !== existing.length) continue;
    const timestamps = existingLinks.flatMap((link) => {
      const value = cache.resolve(link, 'created_at');
      const timestamp =
        typeof value === 'string' ? Date.parse(value) : Number.NaN;
      return Number.isFinite(timestamp) ? [timestamp] : [];
    });
    const oldestHeadTimestamp = timestamps.length
      ? Math.min(...timestamps)
      : head.cursor;
    const merged = new Set(existingLinks);
    for (const item of links) {
      const message = readFragment(ChatMessageItemFragment, item);
      if (
        message.chat_id !== chatId ||
        Date.parse(message.created_at) < oldestHeadTimestamp
      )
        continue;
      const key = cache.keyOfEntity({ __typename: 'message', id: message.id });
      if (key) merged.add(key);
    }
    if (merged.size !== existingLinks.length) {
      cache.link(QUERY_ROOT_NAME, head.fieldKey, [...merged]);
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
